import { Badge, Box, Tooltip } from '@mui/material';
import { differenceInCalendarDays, isToday, isTomorrow, isYesterday } from 'date-fns';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

interface DaysIndicatorProps {
    date: string | Date | number;
}

export function DaysIndicator({ date, children }: PropsWithChildren<DaysIndicatorProps>) {
    const { t } = useTranslation();
    const targetDate = new Date(date);
    const today = new Date();
    const diff = differenceInCalendarDays(targetDate, today);
    const absDiff = Math.abs(diff);

    let gradient = '';
    const textColor = '#000';

    if (diff < 0) {
        // Past - Very Faint Gray
        // Using Slate-50 to Slate-100 for background
        gradient = 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)';
    } else {
        // Future / Today
        if (diff === 0) {
            // Today - Critical/Urgent (Red)
            gradient = 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)';
        } else if (diff <= 3) {
            // Very Soon (1-3 days) - Orange-Red
            gradient = 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)';
        } else if (diff <= 7) {
            // Soon (4-7 days) - Amber
            gradient = 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
        } else {
            // Safe (> 7 days) - Emerald (Green)
            gradient = 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
        }
    }

    let tooltipText = '';
    if (isToday(targetDate)) {
        tooltipText = t('common.date.today');
    } else if (isYesterday(targetDate)) {
        tooltipText = t('common.date.yesterday');
    } else if (isTomorrow(targetDate)) {
        tooltipText = t('common.date.tomorrow');
    } else if (diff < 0) {
        tooltipText = t('common.date.daysAgo', { count: absDiff });
    } else {
        tooltipText = t('common.date.daysFromNow', { count: absDiff });
    }

    return (
        <Tooltip title={tooltipText}>
            <Badge
                badgeContent={absDiff}
                max={99}
                overlap='rectangular'
                sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 18, background: gradient, color: textColor, transform: 'translate(120%, -50%)' } }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box>{children}</Box>
            </Badge>
        </Tooltip>
    );
}
